import { BrowserProvider, Contract, JsonRpcProvider, MaxUint256 } from "ethers";
import { tradeErrorMessage } from "@/lib/poolSwap";

const SLIPPAGE_BPS = 100n;
const ERC20 = [
  "function allowance(address,address) view returns (uint256)",
  "function approve(address,uint256) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
];
let abis;
const loadAbis = async () => (abis = abis || (await (await fetch("/contracts/FSCDex.json")).json()));
export const readProvider = new JsonRpcProvider(`${process.env.REACT_APP_BACKEND_URL}/api/rpc`, 77177, { staticNetwork: true });
const deadline = () => BigInt(Math.floor(Date.now() / 1000) + 1200);
const minOut = (v) => (v * (10000n - SLIPPAGE_BPS)) / 10000n;
const same = (a, b) => a.toLowerCase() === b.toLowerCase();

export const findPair = (pairs, a, b) =>
  pairs.find((p) => (same(p.token0.address, a.address) && same(p.token1.address, b.address)) || (same(p.token0.address, b.address) && same(p.token1.address, a.address)));

/** Direct path when a pair exists, otherwise a hop through FMI. */
export function buildPath(pairs, tokenIn, tokenOut, wfmi) {
  if (findPair(pairs, tokenIn, tokenOut)) return [tokenIn.address, tokenOut.address];
  const fmi = { address: wfmi };
  if (!tokenIn.isFmi && !tokenOut.isFmi && findPair(pairs, tokenIn, fmi) && findPair(pairs, fmi, tokenOut)) return [tokenIn.address, wfmi, tokenOut.address];
  return null;
}

export async function quoteSwap(routerAddr, amountIn, path) {
  const { FSCRouter } = await loadAbis();
  const amounts = await new Contract(routerAddr, FSCRouter.abi, readProvider).getAmountsOut(amountIn, path);
  return amounts[amounts.length - 1];
}

/** Reserves of `a` and `b` (raw) in their pair, in that order. */
export const pairReserves = (pair, a) =>
  same(pair.token0.address, a.address) ? [BigInt(pair.reserve0Raw), BigInt(pair.reserve1Raw)] : [BigInt(pair.reserve1Raw), BigInt(pair.reserve0Raw)];

async function signerFor(eip1193) {
  const signer = await new BrowserProvider(eip1193).getSigner();
  return { signer, owner: await signer.getAddress() };
}

async function ensureAllowance(token, signer, owner, spender, amount, onStep, label) {
  const c = new Contract(token, ERC20, signer);
  if ((await c.balanceOf(owner)) < amount) throw new Error(`Insufficient ${label} balance`);
  if ((await c.allowance(owner, spender)) >= amount) return;
  onStep(`Approve ${label} in your wallet…`);
  await (await c.approve(spender, MaxUint256)).wait();
}

async function finish(tx, onStep, msg) {
  onStep(msg);
  await tx.wait();
  return tx.hash;
}

export async function dexSwap({ eip1193, routerAddr, path, tokenIn, tokenOut, amountIn, expectedOut, onStep }) {
  const { FSCRouter } = await loadAbis();
  const { signer, owner } = await signerFor(eip1193);
  const r = new Contract(routerAddr, FSCRouter.abi, signer);
  const min = minOut(expectedOut);
  if (!tokenIn.isFmi) await ensureAllowance(tokenIn.address, signer, owner, routerAddr, amountIn, onStep, tokenIn.symbol);
  onStep("Confirm the swap in your wallet…");
  let tx;
  if (tokenIn.isFmi) tx = await r.swapExactFMIForTokens(min, path, owner, deadline(), { value: amountIn });
  else if (tokenOut.isFmi) tx = await r.swapExactTokensForFMI(amountIn, min, path, owner, deadline());
  else tx = await r.swapExactTokensForTokens(amountIn, min, path, owner, deadline());
  return finish(tx, onStep, "Swapping on FSC…");
}

export async function dexAddLiquidity({ eip1193, routerAddr, tokenA, tokenB, amountA, amountB, onStep }) {
  const { FSCRouter } = await loadAbis();
  const { signer, owner } = await signerFor(eip1193);
  const r = new Contract(routerAddr, FSCRouter.abi, signer);
  const [token, tokenAmt, fmiAmt] = tokenA.isFmi ? [tokenB, amountB, amountA] : [tokenA, amountA, amountB];
  if (tokenA.isFmi || tokenB.isFmi) {
    await ensureAllowance(token.address, signer, owner, routerAddr, tokenAmt, onStep, token.symbol);
    onStep("Confirm the deposit in your wallet…");
    return finish(await r.addLiquidityFMI(token.address, tokenAmt, minOut(tokenAmt), minOut(fmiAmt), owner, deadline(), { value: fmiAmt }), onStep, "Adding liquidity on FSC…");
  }
  await ensureAllowance(tokenA.address, signer, owner, routerAddr, amountA, onStep, tokenA.symbol);
  await ensureAllowance(tokenB.address, signer, owner, routerAddr, amountB, onStep, tokenB.symbol);
  onStep("Confirm the deposit in your wallet…");
  return finish(await r.addLiquidity(tokenA.address, tokenB.address, amountA, amountB, minOut(amountA), minOut(amountB), owner, deadline()), onStep, "Adding liquidity on FSC…");
}

export async function lpBalance(pairAddr, owner) {
  return new Contract(pairAddr, ERC20, readProvider).balanceOf(owner);
}

export async function dexRemoveLiquidity({ eip1193, routerAddr, pair, liquidity, onStep }) {
  const { FSCRouter } = await loadAbis();
  const { signer, owner } = await signerFor(eip1193);
  const lp = new Contract(pair.address, ERC20, signer);
  if ((await lp.allowance(owner, routerAddr)) < liquidity) {
    onStep("Approve the LP shares in your wallet…");
    await (await lp.approve(routerAddr, MaxUint256)).wait();
  }
  const supply = BigInt(pair.totalSupplyRaw);
  const out0 = minOut((liquidity * BigInt(pair.reserve0Raw)) / supply);
  const out1 = minOut((liquidity * BigInt(pair.reserve1Raw)) / supply);
  const r = new Contract(routerAddr, FSCRouter.abi, signer);
  onStep("Confirm the withdrawal in your wallet…");
  const fmiSide = pair.token0.isFmi ? 0 : pair.token1.isFmi ? 1 : -1;
  if (fmiSide === -1) {
    return finish(await r.removeLiquidity(pair.token0.address, pair.token1.address, liquidity, out0, out1, owner, deadline()), onStep, "Withdrawing on FSC…");
  }
  const [token, tokenMin, fmiMin] = fmiSide === 0 ? [pair.token1, out1, out0] : [pair.token0, out0, out1];
  return finish(await r.removeLiquidityFMI(token.address, liquidity, tokenMin, fmiMin, owner, deadline()), onStep, "Withdrawing on FSC…");
}

export const dexErrorMessage = (err) => tradeErrorMessage(err, "Transaction", "this transaction and gas");
